import type { PrismaClient } from '@prisma/client';

export type AccountType = 'asset' | 'liability' | 'equity' | 'revenue' | 'expense';

export interface SeedAccount {
  code: string;
  name: string;
  type: AccountType;
  subtype?: string;
  systemKey?: string;
  cashFlowCategory?: 'operating' | 'investing' | 'financing';
  description?: string;
}

/**
 * Standard chart for an IT services and trading company in Uganda (PLAN.md section 7.2).
 * Accounts with a systemKey are locked: the posting rules depend on them.
 * Bank, mobile-money and cash accounts are created per financial account (banking module).
 */
export const CHART: SeedAccount[] = [
  // Assets
  {
    code: '1210',
    name: 'Accounts Receivable',
    type: 'asset',
    subtype: 'receivable',
    systemKey: 'AR',
    cashFlowCategory: 'operating',
  },
  {
    code: '1230',
    name: 'WHT Receivable',
    type: 'asset',
    subtype: 'tax',
    systemKey: 'WHT_REC',
    cashFlowCategory: 'operating',
  },
  {
    code: '1240',
    name: 'VAT Input',
    type: 'asset',
    subtype: 'tax',
    systemKey: 'VAT_IN',
    cashFlowCategory: 'operating',
  },
  {
    code: '1300',
    name: 'Inventory (IT Equipment)',
    type: 'asset',
    subtype: 'inventory',
    systemKey: 'INVENTORY',
    cashFlowCategory: 'operating',
  },
  {
    code: '1500',
    name: 'Fixed Assets',
    type: 'asset',
    subtype: 'fixed_asset',
    systemKey: 'FIXED_ASSETS',
    cashFlowCategory: 'investing',
  },
  {
    code: '1590',
    name: 'Accumulated Depreciation',
    type: 'asset',
    subtype: 'fixed_asset',
    systemKey: 'ACC_DEPR',
    cashFlowCategory: 'investing',
  },
  // Liabilities
  {
    code: '2100',
    name: 'Accounts Payable',
    type: 'liability',
    subtype: 'payable',
    systemKey: 'AP',
    cashFlowCategory: 'operating',
  },
  {
    code: '2200',
    name: 'VAT Output',
    type: 'liability',
    subtype: 'tax',
    systemKey: 'VAT_OUT',
    cashFlowCategory: 'operating',
  },
  {
    code: '2210',
    name: 'WHT Payable',
    type: 'liability',
    subtype: 'tax',
    systemKey: 'WHT_PAY',
    cashFlowCategory: 'operating',
  },
  {
    code: '2220',
    name: 'PAYE / NSSF Payable',
    type: 'liability',
    subtype: 'tax',
    systemKey: 'PAYROLL_TAX',
    cashFlowCategory: 'operating',
  },
  {
    code: '2300',
    name: 'Customer Advances',
    type: 'liability',
    subtype: 'advance',
    systemKey: 'CUST_ADV',
    cashFlowCategory: 'operating',
  },
  {
    code: '2400',
    name: 'Accrued Expenses',
    type: 'liability',
    subtype: 'accrual',
    systemKey: 'ACCRUALS',
    cashFlowCategory: 'operating',
  },
  // Equity
  {
    code: '3100',
    name: "Owner's Capital",
    type: 'equity',
    subtype: 'capital',
    systemKey: 'CAPITAL',
    cashFlowCategory: 'financing',
  },
  {
    code: '3200',
    name: 'Retained Earnings',
    type: 'equity',
    subtype: 'retained',
    systemKey: 'RETAINED',
    cashFlowCategory: 'financing',
  },
  {
    code: '3300',
    name: 'Opening Balance Equity',
    type: 'equity',
    subtype: 'opening',
    systemKey: 'OPENING_EQ',
    cashFlowCategory: 'financing',
  },
  // Revenue
  {
    code: '4100',
    name: 'Software Development Revenue',
    type: 'revenue',
    subtype: 'sales',
    systemKey: 'REV_SOFTWARE',
  },
  {
    code: '4200',
    name: 'IT Consultancy Revenue',
    type: 'revenue',
    subtype: 'sales',
    systemKey: 'REV_CONSULT',
  },
  {
    code: '4300',
    name: 'Hosting & Domains Revenue',
    type: 'revenue',
    subtype: 'sales',
    systemKey: 'REV_HOSTING',
  },
  {
    code: '4400',
    name: 'Equipment Sales',
    type: 'revenue',
    subtype: 'sales',
    systemKey: 'REV_EQUIP',
  },
  { code: '4900', name: 'Other Income', type: 'revenue', subtype: 'other', systemKey: 'REV_OTHER' },
  {
    code: '4950',
    name: 'Foreign Exchange Gain',
    type: 'revenue',
    subtype: 'other',
    systemKey: 'FX_GAIN',
  },
  // Cost of sales
  {
    code: '5100',
    name: 'Cost of Equipment Sold',
    type: 'expense',
    subtype: 'cogs',
    systemKey: 'COGS_EQUIP',
  },
  {
    code: '5200',
    name: 'Hosting & Domain Costs',
    type: 'expense',
    subtype: 'cogs',
    systemKey: 'COGS_HOSTING',
  },
  {
    code: '5300',
    name: 'Subcontractor Costs',
    type: 'expense',
    subtype: 'cogs',
    systemKey: 'COGS_SUBCON',
  },
  // Operating expenses
  { code: '6100', name: 'Salaries & Wages', type: 'expense', subtype: 'opex' },
  { code: '6200', name: 'Rent', type: 'expense', subtype: 'opex' },
  { code: '6300', name: 'Utilities & Internet', type: 'expense', subtype: 'opex' },
  { code: '6400', name: 'Transport & Travel', type: 'expense', subtype: 'opex' },
  { code: '6500', name: 'Marketing & Advertising', type: 'expense', subtype: 'opex' },
  { code: '6600', name: 'Software & Subscriptions', type: 'expense', subtype: 'opex' },
  { code: '6700', name: 'Professional Fees', type: 'expense', subtype: 'opex' },
  {
    code: '6800',
    name: 'Bank & Mobile Money Charges',
    type: 'expense',
    subtype: 'opex',
    systemKey: 'BANK_CHARGES',
  },
  {
    code: '6900',
    name: 'Depreciation',
    type: 'expense',
    subtype: 'opex',
    systemKey: 'DEPRECIATION',
  },
  { code: '6950', name: 'Office Supplies', type: 'expense', subtype: 'opex' },
  {
    code: '6980',
    name: 'Foreign Exchange Loss',
    type: 'expense',
    subtype: 'other',
    systemKey: 'FX_LOSS',
  },
  { code: '6990', name: 'Other Expenses', type: 'expense', subtype: 'opex' },
];

/** Control accounts are maintained by their sub-ledgers; manual journals may not touch them. */
export const CONTROL_ACCOUNT_KEYS = ['AR', 'AP', 'CUST_ADV'];

export async function ensureChart(db: PrismaClient, companyId: number): Promise<void> {
  for (const a of CHART) {
    await db.account.upsert({
      where: { companyId_code: { companyId, code: a.code } },
      create: {
        companyId,
        code: a.code,
        name: a.name,
        type: a.type,
        subtype: a.subtype,
        systemKey: a.systemKey,
        isSystem: !!a.systemKey,
        cashFlowCategory: a.cashFlowCategory,
      },
      update: {},
    });
  }
}

export const isDebitNormal = (type: string) => type === 'asset' || type === 'expense';
